import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Card, Button, Input, Table, Space, Modal, message, Badge, Dropdown, Tag, Menu, Tooltip, Typography } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, CopyOutlined, CheckOutlined, MoreOutlined, ExclamationCircleOutlined, DownOutlined, RocketOutlined, UploadOutlined, AppstoreAddOutlined, ExportOutlined, ImportOutlined } from "@ant-design/icons";
import { useNavigate, Link } from "react-router-dom";
import { useLoadList, useDelete, useQuickSearch } from "../../../hooks";
import { useTableSelection } from "../../../hooks";
import { scheduleService } from "../../../api/enhancedApi";
import { formatDate, formatDateTime } from "../../../utils/dateUtils";
import AircraftTag from "../../../components/AircraftTag";
import ScheduleDraftFilter from "./components/ScheduleDraftFilter";
import { useAuth } from "../../../hooks/useAuth";
import PermissionGuard from "../../../components/auth/PermissionGuard";
import { ExportButton, BatchExportButton } from "../../../components/export";
import { exportApi } from "../../../api/exportApi";
import { handleExportDownload } from "../../../utils/exportUtils";
import ScheduleActivityDrawer from "../../../components/common/ScheduleActivityDrawer";
import { ScheduleEditor } from "../ScheduleEditor/ScheduleEditorPage";
import CollapsibleFilterBar from "../../../components/common/CollapsibleFilterBar";
import ScheduleTag from "../shared/ScheduleTag";
import { useScheduleTags } from "../shared/useScheduleTags";

const { confirm } = Modal;

const ScheduleDraftList = () => {
    const navigate = useNavigate();
    // 获取权限相关功能
    const { hasPermission } = useAuth();
    const { definitionMap: scheduleTagDefinitionMap, loadError: scheduleTagLoadError } = useScheduleTags({ includeInactive: true });

    // 状态定义
    const [filterParams, setFilterParams] = useState({});
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 30,
    });
    const [deleteVisible, setDeleteVisible] = useState(false);
    const [draftToDelete, setDraftToDelete] = useState(null);


    // 活动记录抽屉状态
    const [activityDrawerVisible, setActivityDrawerVisible] = useState(false);
    const [selectedScheduleForActivity, setSelectedScheduleForActivity] = useState(null);

    // 编辑器 Modal 状态
    const [editorVisible, setEditorVisible] = useState(false);
    const [editorScheduleId, setEditorScheduleId] = useState(null);

    // 使用自定义Hook加载数据
    const { data, isLoading, refetch } = useLoadList("schedules", scheduleService.getScheduleList, {
        page: pagination.current,
        page_size: pagination.pageSize,
        status: "DRAFT", // 明确只查询DRAFT状态
        ...filterParams,
    });

    // 使用自定义Hook处理表格选择
    const { selectedRowKeys, selectedRows, tableRef, rowSelection, getIndexColumn, getRowProps, clearSelection } = useTableSelection({
        rowKey: "id",
        dataSource: data?.items || [],
        pagination,
    });

    // 删除计划草案的Hook
    const deleteMutation = useDelete("draftList", scheduleService.deleteSchedule, {
        onSuccess: () => {
            message.success("删除成功");
            setDeleteVisible(false);
            refetch();
        },
    });

    // 处理单个导出
    const handleExport = async (record) => {
        try {
            message.loading("正在准备导出...", 0);

            // 使用通用的导出资源API
            const response = await exportApi.exportResource("schedule", record.id);

            message.destroy(); // 清除加载消息

            // 处理导出响应
            if (response.data && response.data.download_url) {
                // 直接下载
                handleExportDownload(response.data.download_url, response.data.file_name);
                message.success("导出成功");
            } else if (response.data && response.data.task_id) {
                // 异步任务需要轮询
                message.info("导出任务已提交，将在后台处理并自动下载");

                // 这里可以添加轮询逻辑，但为简化代码先省略
                // 实际项目中应该实现完整的轮询逻辑
            } else {
                message.error("导出失败: 服务器返回格式不正确");
            }
        } catch (error) {
            message.destroy();
            message.error(`导出失败: ${error.message || "未知错误"}`);
        }
    };

    // 表格列定义
    const columns = [
        getIndexColumn("序号", 60),
        {
            title: "草案名称",
            dataIndex: "name",
            key: "name",
            ellipsis: true,
            width: 180,
            render: (text, record) => (
                <a
                    onClick={(e) => {
                        e.preventDefault();
                        openEditorModal(record.id);
                    }}
                >
                    {text || "未命名草案"}
                </a>
            ),
        },
        {
            title: "标签",
            dataIndex: "tags",
            key: "tags",
            width: 150,
            render: (tags) => (
                <>
                    {tags &&
                        Array.isArray(tags) &&
                        tags.map((tag, index) => (
                            <ScheduleTag
                                key={`${tag}-${index}`}
                                name={tag}
                                definitionMap={scheduleTagDefinitionMap}
                                loadError={scheduleTagLoadError}
                            />
                        ))}
                </>
            ),
        },
        {
            title: "时间范围",
            key: "date_range",
            width: 160,
            render: (_, record) => {
                if (!record.start_date && !record.end_date) return "无时间范围";
                const start = record.start_date ? formatDate(record.start_date) : "未设置";
                const end = record.end_date ? formatDate(record.end_date) : "未设置";
                return `${start} 至 ${end}`;
            },
        },
        {
            title: "飞机",
            key: "aircraft",
            width: 120,
            render: (_, record) => {
                let registration = "未分配";
                if (record.aircraft && record.aircraft.registration) {
                    registration = record.aircraft.registration;
                } else if (record.aircraft_reg) {
                    registration = record.aircraft_reg;
                }

                return <AircraftTag registration={registration} />;
            },
        },
        {
            title: "航班数量",
            key: "flight_count",
            width: 100,
            align: "right",
            render: (_, record) => {
                if (record.flights_count !== undefined) {
                    return record.flights_count;
                } else if (record.flight_count !== undefined) {
                    return record.flight_count;
                } else if (record.flights && Array.isArray(record.flights)) {
                    return record.flights.length;
                } else {
                    return 0;
                }
            },
        },
        {
            title: "状态",
            dataIndex: "status",
            key: "status",
            width: 100,
            render: (text) => {
                const statusMap = {
                    DRAFT: { status: "default", text: "草稿" },
                    SUBMITTED: { status: "processing", text: "已提交" },
                    APPROVED: { status: "success", text: "已批准" },
                    REJECTED: { status: "error", text: "已拒绝" },
                    PUBLISHED: { status: "success", text: "已发布" },
                };

                const status = statusMap[text] || { status: "default", text: "草稿" };

                return <Badge className="status-badge" status={status.status} text={status.text} />;
            },
        },
        {
            title: "创建时间",
            dataIndex: "created_at",
            key: "created_at",
            width: 120,
            render: (text) => (text ? formatDateTime(text) : "未知"),
        },
        {
            title: "更新时间",
            dataIndex: "updated_at",
            key: "updated_at",
            width: 120,
            render: (text) => (text ? formatDateTime(text) : "未知"),
        },
        {
            title: "操作人",
            key: "last_operator",
            width: 120,
            render: (_, record) => {
                if (record.last_operator_name) {
                    const operatorTypeMap = {
                        USER: { color: "blue", text: "用户" },
                        SYSTEM: { color: "green", text: "系统" },
                        AI_AGENT: { color: "purple", text: "AI" },
                        API_CLIENT: { color: "orange", text: "API" },
                    };
                    const typeConfig = operatorTypeMap[record.last_operator_type] || { color: "default", text: "未知" };

                    return (
                        <Tooltip title={`${typeConfig.text}: ${record.last_operator_name} (点击查看活动记录)`}>
                            <Tag color={typeConfig.color} size="small" style={{ cursor: "pointer" }} onClick={() => handleOperatorClick(record)}>
                                {record.last_operator_name}
                            </Tag>
                        </Tooltip>
                    );
                }
                return <span style={{ color: "#999" }}>-</span>;
            },
        },
        {
            title: "操作",
            key: "action",
            fixed: "right",
            width: 160,
            render: (_, record) => (
                <Space size="small" className="table-actions">
                    <PermissionGuard permission="schedule:update">
                        <Link to={`/flightSchedule/editor/${record.id}`}>
                            <Button type="link" size="small" icon={<EditOutlined />}>
                                编辑
                            </Button>
                        </Link>
                    </PermissionGuard>
                    <PermissionGuard permission="schedule:update">
                        <Button type="link" size="small" icon={<CheckOutlined />} onClick={() => submitDraft(record)}>
                            提交
                        </Button>
                    </PermissionGuard>
                    <Dropdown
                        menu={{
                            items: [
                                {
                                    key: "view",
                                    label: "查看详情",
                                    icon: <EyeOutlined />,
                                    onClick: () => viewDraft(record),
                                },
                                {
                                    key: "duplicate",
                                    label: "复制草案",
                                    icon: <CopyOutlined />,
                                    onClick: () => duplicateDraft(record),
                                    disabled: !hasPermission("schedule:create"),
                                },
                                {
                                    key: "export",
                                    label: "导出草案",
                                    icon: <ExportOutlined />,
                                    onClick: () => handleExport(record),
                                    disabled: !hasPermission("schedule:read"),
                                },
                                {
                                    key: "delete",
                                    label: "删除草案",
                                    icon: <DeleteOutlined />,
                                    danger: true,
                                    onClick: () => confirmDeleteDraft(record),
                                    disabled: !hasPermission("schedule:delete"),
                                },
                            ],
                        }}
                        placement="bottomRight"
                        trigger={["click"]}
                    >
                        <Button type="link" size="small" icon={<MoreOutlined />} />
                    </Dropdown>
                </Space>
            ),
        },
    ];

    // 处理表格分页变化
    const handleTableChange = (newPagination, filters, sorter) => {
        setPagination(newPagination);
    };

    // 处理搜索
    const handleSearch = (values) => {
        const params = { ...values }; // 先复制所有值

        // 检查并转换 tags 字段（如果存在且是数组）
        if (params.tags && Array.isArray(params.tags)) {
            params.tags = params.tags.join(",");
        }

        // 检查并转换 aircraft_id 字段
        if (params.aircraft_id && Array.isArray(params.aircraft_id)) {
            params.aircraft_id = params.aircraft_id.join(",");
        }
        // 其他参数如 name, dateRange 等如果存在于 values 中，则已在 params 中

        setFilterParams(params);
        setPagination({
            ...pagination,
            current: 1, // 重置到第一页
        });
    };

    // 重置搜索
    const handleReset = () => {
        setFilterParams({});
        setPagination({
            ...pagination,
            current: 1,
        });
    };

    // 筛选摘要项
    const filterSummaryItems = useMemo(() => {
        const items = [];
        if (filterParams.name) items.push({ label: "草案名称", value: filterParams.name });
        if (filterParams.aircraft_id) {
            const ids = filterParams.aircraft_id.split(",");
            items.push({ label: "飞机", value: `${ids.length}架` });
        }
        if (filterParams.tags) {
            items.push({ label: "标签", value: filterParams.tags });
        }
        return items;
    }, [filterParams]);

    // 快捷搜索
    const quickSearch = useQuickSearch({ setFilterParams, setPagination });

    // 创建新草案
    const createNewDraft = () => {
        // 检查创建权限
        if (!hasPermission("schedule:create")) {
            message.error("您没有创建航班计划的权限");
            return;
        }
        // navigate("/flightSchedule/editor");
        openEditorModal(null); // Open editor for new draft
    };

    // 查看草案
    const viewDraft = (draft) => {
        navigate(`/flightSchedule/editor/${draft.id}`);
    };

    // 编辑草案
    const editDraft = (draft) => {
        // 检查更新权限
        if (!hasPermission("schedule:update")) {
            message.error("您没有编辑航班计划的权限");
            return;
        }
        navigate(`/flightSchedule/editor/${draft.id}`);
    };

    // 提交草案
    const submitDraft = (draft) => {
        // 检查更新权限
        if (!hasPermission("schedule:update")) {
            message.error("您没有提交航班计划的权限");
            return;
        }

        // 先检查是否进行过对比
        const checkComparisonBeforeSubmit = async () => {
            try {
                // 获取计划详情，注意需要从 .data 取出实际数据对象
                const response = await scheduleService.getScheduleDetail(draft.id);
                const detailData = response.data;
                // 检查是否有对比结果（注意字段为 parameter.flight_compare_result）
                if (!detailData.parameter?.parameter_json?.flight_compare_result) {
                    // 没有对比结果，提示必须先对比
                    Modal.confirm({
                        title: "未进行航班对比",
                        icon: <ExclamationCircleOutlined />,
                        content: "提交前必须先与执行计划进行对比。是否前往编辑器进行对比操作？",
                        okText: "前往对比",
                        cancelText: "取消",
                        onOk: () => {
                            // 导航到编辑器并切换到对比标签
                            navigate(`/flightSchedule/editor/${draft.id}`, { state: { activeTab: "flightCompare" } });
                        },
                    });
                } else {
                    // 已有对比结果，直接提交
                    confirmSubmit();
                }
            } catch (error) {
                console.error("检查对比结果失败:", error);
                // 出错时提示用户
                message.error("检查对比结果失败，请重试");
            }
        };

        // 确认提交的函数
        const confirmSubmit = () => {
            confirm({
                title: "确认提交",
                icon: <ExclamationCircleOutlined />,
                content: `确定要提交草案"${draft.name || "未命名草案"}"进行审批吗？`,
                okText: "确认",
                cancelText: "取消",
                onOk: async () => {
                    try {
                        // 直接调用API修改状态为已提交
                        await scheduleService.submitDraftForApproval(draft.id);
                        message.success("草案已成功提交");
                        refetch(); // 刷新列表
                    } catch (error) {
                        message.error(`提交失败: ${error.message || "未知错误"}`);
                    }
                },
            });
        };

        // 执行检查
        checkComparisonBeforeSubmit();
    };

    // 复制草案
    const duplicateDraft = (draft) => {
        // 检查创建权限
        if (!hasPermission("schedule:create")) {
            message.error("您没有创建航班计划的权限");
            return;
        }

        // TODO: 实现复制草案逻辑
        message.success("复制功能正在开发中");
    };

    // 确认删除草案
    const confirmDeleteDraft = (draft) => {
        // 检查删除权限
        if (!hasPermission("schedule:delete")) {
            message.error("您没有删除航班计划的权限");
            return;
        }

        setDraftToDelete(draft);
        setDeleteVisible(true);
    };

    // 执行删除草案
    const handleDeleteDraft = () => {
        if (!draftToDelete) return;
        deleteMutation.mutate(draftToDelete.id);
    };

    // 批量删除草案
    const handleBatchDelete = () => {
        // 检查删除权限
        if (!hasPermission("schedule:delete")) {
            message.error("您没有删除航班计划的权限");
            return;
        }

        if (selectedRowKeys.length === 0) return;

        confirm({
            title: "确认批量删除",
            icon: <ExclamationCircleOutlined />,
            content: `确定要删除选中的 ${selectedRowKeys.length} 个草案吗？此操作不可恢复。`,
            okText: "确认",
            okType: "danger",
            cancelText: "取消",
            onOk() {
                // 串行执行删除操作
                Promise.all(selectedRowKeys.map((key) => scheduleService.deleteSchedule(key)))
                    .then(() => {
                        message.success(`成功删除 ${selectedRowKeys.length} 个草案`);
                        clearSelection();
                        refetch();
                    })
                    .catch((error) => {
                        message.error(`批量删除失败: ${error.message}`);
                    });
            },
        });
    };

    // 批量提交草案
    const handleBatchSubmit = () => {
        // 检查更新权限
        if (!hasPermission("schedule:update")) {
            message.error("您没有提交航班计划的权限");
            return;
        }

        if (selectedRowKeys.length === 0) return;

        // 检查所选计划是否都进行过对比
        const checkBatchComparisonBeforeSubmit = async () => {
            try {
                // 保存未进行对比的计划名称
                const notComparedSchedules = [];

                // 先显示加载消息
                const loadingMsg = message.loading("正在检查计划对比状态...", 0);

                // 批量获取详细计划信息
                await Promise.all(
                    selectedRows.map(async (draft) => {
                        try {
                            // 获取计划详情，注意需要从 .data 取出实际数据对象
                            const response = await scheduleService.getScheduleDetail(draft.id, { limit: 1 }); // 因为明细只需要加载一条，这里我们用到的只有 parameter 信息
                            const detailData = response.data;
                            // 检查是否有对比结果（注意字段为 parameter.flight_compare_result）
                            if (!detailData.parameter?.flight_compare_result) {
                                notComparedSchedules.push(draft.name || `未命名草案(ID: ${draft.id})`);
                            }
                        } catch (err) {
                            console.error(`获取计划 ${draft.id} 详情失败:`, err);
                            // 如果获取失败，也将其添加到未对比列表
                            notComparedSchedules.push(`${draft.name || "未命名草案"}(获取详情失败)`);
                        }
                    }),
                );

                // 关闭加载消息
                loadingMsg();

                if (notComparedSchedules.length > 0) {
                    // 有计划未进行对比，不允许提交
                    Modal.error({
                        title: "无法提交",
                        content: (
                            <div>
                                <p>以下计划未与执行计划进行对比:</p>
                                <ul style={{ maxHeight: "200px", overflow: "auto" }}>
                                    {notComparedSchedules.map((name, index) => (
                                        <li key={index}>{name}</li>
                                    ))}
                                </ul>
                                <p>提交前必须先进行对比，请对以上计划进行对比后再提交。</p>
                            </div>
                        ),
                    });
                } else {
                    // 所有计划都进行过对比，直接进入确认提交流程
                    confirmBatchSubmit();
                }
            } catch (error) {
                console.error("批量检查对比结果失败:", error);
                message.error("检查计划对比状态失败");
            }
        };

        // 确认批量提交的函数
        const confirmBatchSubmit = () => {
            confirm({
                title: "确认批量提交",
                icon: <ExclamationCircleOutlined />,
                content: `确定要提交选中的 ${selectedRowKeys.length} 个草案进行审批吗？`,
                okText: "确认",
                cancelText: "取消",
                onOk: async () => {
                    try {
                        // 串行执行提交操作
                        const results = await Promise.allSettled(selectedRowKeys.map((id) => scheduleService.submitDraftForApproval(id)));

                        // 统计成功和失败的数量
                        const successCount = results.filter((r) => r.status === "fulfilled").length;
                        const failCount = results.length - successCount;

                        if (failCount === 0) {
                            message.success(`成功提交 ${successCount} 个草案`);
                        } else {
                            message.warning(`提交完成，${successCount} 个成功，${failCount} 个失败`);
                        }

                        clearSelection();
                        refetch();
                    } catch (error) {
                        message.error(`批量提交失败: ${error.message || "未知错误"}`);
                    }
                },
            });
        };

        // 执行批量检查
        checkBatchComparisonBeforeSubmit();
    };

    // 处理点击操作人显示活动记录
    const handleOperatorClick = (schedule) => {
        setSelectedScheduleForActivity(schedule);
        setActivityDrawerVisible(true);
    };

    // 关闭活动记录抽屉
    const handleCloseActivityDrawer = () => {
        setActivityDrawerVisible(false);
        setSelectedScheduleForActivity(null);
    };

    // 打开编辑器 Modal
    const openEditorModal = (id) => {
        setEditorScheduleId(id);
        setEditorVisible(true);
    };

    // 关闭编辑器 Modal
    const closeEditorModal = (shouldRefresh = false) => {
        setEditorVisible(false);
        setEditorScheduleId(null);
        // 只有明确要求刷新时才刷新 (即发生过保存操作)
        if (shouldRefresh === true) {
            refetch();
        }
    };

    // 处理 Modal 取消/遮罩点击
    const handleModalCancel = () => {
        // 检查全局编辑器状态
        if (window.currentScheduleEditor) {
            // 如果有未保存更改
            if (window.currentScheduleEditor.isDirty) {
                confirm({
                    title: "确认关闭",
                    icon: <ExclamationCircleOutlined />,
                    content: "您有未保存的更改，确定要关闭吗？",
                    okText: "确认关闭",
                    cancelText: "取消",
                    onOk: () => {
                        // 即使有未保存更改，如果之前保存过，也应该刷新
                        closeEditorModal(window.currentScheduleEditor.hasSaved);
                    },
                });
                return;
            }
            // 如果没有未保存更改，但也可能保存过（hasSaved），需要传递状态
            closeEditorModal(window.currentScheduleEditor.hasSaved);
        } else {
            // 兜底
            closeEditorModal();
        }
    };

    return (
        <div className="page-container">
            {/* 页面标题 */}
            <div className="page-header-compact">
                <div className="page-title-compact">航班计划草案</div>
                <div className="page-action-compact">
                    <Space>
                        <PermissionGuard permission="schedule:create">
                            <Button type="primary" icon={<PlusOutlined />} onClick={createNewDraft}>
                                新建草案
                            </Button>
                        </PermissionGuard>
                    </Space>
                </div>
            </div>

            {/* 搜索过滤 */}
            <CollapsibleFilterBar
                summaryItems={filterSummaryItems}
                onReset={handleReset}
                drawerTitle="航班计划草案 - 筛选条件"
                quickControls={
                    <Input
                        placeholder="搜索名称/飞机注册号..."
                        style={{ width: 220, height: 26 }}
                        {...quickSearch.inputProps}
                    />
                }
            >
                <ScheduleDraftFilter onSearch={handleSearch} onReset={handleReset} autoApply />
            </CollapsibleFilterBar>

            {/* 选中项统计，平铺批量操作按钮 */}
            {selectedRowKeys.length > 0 && (
                <div className="batch-operations-info">
                    <Space size="small">
                        <span>
                            已选择 <span className="selected-count">{selectedRowKeys.length}</span> 项
                        </span>
                        <Button type="link" onClick={clearSelection}>
                            清空
                        </Button>
                        <PermissionGuard permission="schedule:update">
                            <Button type="link" onClick={handleBatchSubmit} disabled={!hasPermission("schedule:update")}>
                                批量提交
                            </Button>
                        </PermissionGuard>
                        <PermissionGuard permission="schedule:delete">
                            <Button type="link" danger onClick={handleBatchDelete} disabled={!hasPermission("schedule:delete")}>
                                批量删除
                            </Button>
                        </PermissionGuard>
                        <PermissionGuard permission="schedule:read">
                            <BatchExportButton resourceType="schedule" resourceIds={selectedRowKeys} buttonText="批量导出" options={{}} buttonProps={{ type: "link" }} />
                        </PermissionGuard>
                    </Space>
                </div>
            )}

            {/* 计划草案列表 */}
            <Card className="data-card">
                <Table
                    ref={tableRef}
                    size="small"
                    rowSelection={hasPermission("schedule:delete") || hasPermission("schedule:update") ? rowSelection : null}
                    columns={columns}
                    dataSource={data?.items || []}
                    rowKey="id"
                    pagination={{
                        ...pagination,
                        total: data?.total || 0,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `共 ${total} 条记录`,
                    }}
                    loading={isLoading}
                    onChange={handleTableChange}
                    scroll={{ x: 1200 }}
                    onRow={getRowProps}
                />
            </Card>

            {/* 删除确认弹窗 */}
            <Modal title="确认删除" open={deleteVisible} onOk={handleDeleteDraft} onCancel={() => setDeleteVisible(false)} okText="确认删除" cancelText="取消" okButtonProps={{ danger: true }}>
                <p>确定要删除航班计划草案 "{draftToDelete?.name || "未命名草案"}" 吗？</p>
                <p>此操作不可恢复。</p>
            </Modal>

            {/* 活动记录抽屉 */}
            <ScheduleActivityDrawer visible={activityDrawerVisible} onClose={handleCloseActivityDrawer} scheduleId={selectedScheduleForActivity?.id} scheduleName={selectedScheduleForActivity?.name} title="活动记录" />

            {/* 计划编辑器 Modal */}
            <Modal title={null} open={editorVisible} footer={null} className="xl-modal" style={{ top: 20 }} onCancel={handleModalCancel} closable={false} destroyOnClose maskClosable={true} bodyStyle={{ padding: 0 }}>
                {editorVisible && <ScheduleEditor scheduleId={editorScheduleId} mode="modal" onClose={closeEditorModal} />}
            </Modal>
        </div>
    );
};

export default ScheduleDraftList;
